<script setup>
import { useSeo } from '@/composables/useSeo'

useSeo('privacy')
import { ref, onMounted, onUnmounted } from 'vue'

const lastUpdated = '10 Février 2025'
const activeSection = ref('collection')

const sections = [
  { id: 'collection', titleKey: 'privacy.sections.collection.title' },
  { id: 'usage', titleKey: 'privacy.sections.usage.title' },
  { id: 'sharing', titleKey: 'privacy.sections.sharing.title' },
  { id: 'security', titleKey: 'privacy.sections.security.title' },
  { id: 'rights', titleKey: 'privacy.sections.rights.title' },
  { id: 'cookies', titleKey: 'privacy.sections.cookies.title' },
  { id: 'contact', titleKey: 'privacy.sections.contact.title' }
]

const scrollTo = (id) => {
  const el = document.getElementById(id)
  if (el) {
    const y = el.getBoundingClientRect().top + window.pageYOffset - 100
    window.scrollTo({ top: y, behavior: 'smooth' })
    activeSection.value = id
  }
}

// Simple spy scroll logic
const handleScroll = () => {
  const scrollPosition = window.scrollY + 150
  for (const section of sections) {
    const element = document.getElementById(section.id)
    if (element && element.offsetTop <= scrollPosition) {
      activeSection.value = section.id
    }
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div class="bg-slate-50 min-h-screen pb-24">
    
    <!-- Header -->
    <div class="bg-white border-b border-slate-200 pt-32 pb-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 class="text-4xl font-bold text-slate-900 mb-4">{{ $t('privacy.title') }}</h1>
        <p class="text-slate-500">
          {{ $t('privacy.updated') }} {{ lastUpdated }}
        </p>
      </div>
    </div>

    <!-- Content Layout -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
      <div class="lg:grid lg:grid-cols-12 gap-12">
        
        <!-- Sidebar Navigation (Hidden on mobile) -->
        <aside class="hidden lg:block lg:col-span-3">
          <nav class="sticky top-32 space-y-1">
            <a v-for="section in sections" :key="section.id"
               @click.prevent="scrollTo(section.id)"
               href="#"
               class="block py-2 px-3 text-sm font-medium border-l-2 transition-colors duration-200"
               :class="activeSection === section.id 
                 ? 'border-primary-700 text-primary-700 bg-purple-50' 
                 : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'">
              {{ $t(section.titleKey) }}
            </a>
          </nav>
        </aside>

        <!-- Main Text -->
        <main class="lg:col-span-9 bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-slate-200">
          <div class="prose prose-slate prose-lg max-w-none prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-primary-700">
            
            <p class="lead">{{ $t('privacy.intro') }}</p>

            <section v-for="section in sections" :key="section.id" :id="section.id" class="scroll-mt-32 mb-12">
              <h3>{{ $t(section.titleKey) }}</h3>
              <div v-html="$t(`privacy.sections.${section.id}.content`)"></div>
            </section>

          </div>
        </main>

      </div>
    </div>
  </div>
</template>

<style scoped>
/* Smooth Scroll Behavior handled by script, but ensure html has scroll-behavior: smooth in global css if preferred */
</style>